import React from 'react'
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Upload,
  UploadFile
} from 'antd'
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { appApi } from 'api/app'
import { horsesApi } from 'api/horses'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { IAddressSuggestion } from 'models/app'
import {
  EHorseStatus,
  ESex,
  IHorseFormInformation,
  IPopulatedHorseWithoutCount,
  IPreferredCommunication,
  IUpdateHorseRequest,
  localeSex
} from 'models/horse'
import { IMAGES_URL } from 'shared/constants/urls'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { normalizePhone } from 'shared/utils/normalizePhone'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'

const MAX_IMAGES = 6
const SEARCH_DELAY = 500

const communicationLabels: Record<keyof IPreferredCommunication, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  calls: 'Звонки',
  sms: 'СМС'
}

const communicationKeys = Object.keys(
  communicationLabels
) as (keyof IPreferredCommunication)[]

// в fileList лежат как новые файлы (originFileObj), так и уже сохранённые на сервере (только url)
const getRawFile = (file?: UploadFile): File | undefined => {
  const raw = file?.originFileObj ?? file
  return raw instanceof File ? raw : undefined
}

const getExistingFileName = (file: UploadFile): string | null =>
  !getRawFile(file) && file.url ? file.url.replace(IMAGES_URL, '') : null

interface IHorseFormProps {
  horse: IPopulatedHorseWithoutCount
  onSaved: () => void
}

export const HorseForm: React.FC<IHorseFormProps> = ({ horse, onSaved }) => {
  const [form] = Form.useForm()

  const [formInformation, setFormInformation] =
    React.useState<IHorseFormInformation | null>(null)
  const [addresses, setAddresses] = React.useState<IAddressSuggestion[]>([])
  const [documents, setDocuments] = React.useState(horse.documents ?? [])
  const [toRemoveDocuments, setToRemoveDocuments] = React.useState<string[]>([])
  const [toRemoveVideoCover, setToRemoveVideoCover] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const videoCover = horse.video_cover && horse.video_cover !== toRemoveVideoCover
    ? horse.video_cover
    : null

  // в select адрес хранится строкой JSON: из неё сразу собирается location для запроса
  const currentLocation = React.useMemo(
    () =>
      horse.location
        ? {
          value: JSON.stringify({
            name: horse.location.name,
            lat: horse.location.lat,
            lng: horse.location.lng
          }),
          label: horse.location.name
        }
        : null,
    [horse.location]
  )

  React.useEffect(() => {
    const getFormInformation = async () => {
      try {
        const response = await horsesApi.getFormInformation()
        setFormInformation(response.data)
      } catch (e) {
        showServerError(e)
      }
    }
    getFormInformation()
  }, [])

  React.useEffect(() => {
    setDocuments(horse.documents ?? [])
    setToRemoveDocuments([])
    setToRemoveVideoCover(null)
    form.setFieldsValue({
      name: horse.name,
      sex: horse.sex,
      breed: horse.breed?._id,
      suit: horse.suit?._id,
      category: horse.category?._id,
      birthDate: horse.birthDate ? dayjs(horse.birthDate) : undefined,
      withersHeight: horse.withersHeight,
      price: horse.price,
      desc: horse.desc ?? '',
      location: currentLocation?.value,
      reg_number: horse.reg_number ?? '',
      genealogy: horse.genealogy ?? '',
      birthplace: horse.birthplace ?? '',
      competition_history: horse.competition_history ?? [],
      newDocuments: [],
      tel: horse.communication?.tel ? phoneMaskStatic(horse.communication.tel) : '',
      preferredCommunication: communicationKeys.filter(
        (key) => horse.communication?.preferredCommunication?.[key]
      ),
      images: horse.images.map((image) => ({
        uid: image,
        name: image,
        status: 'done',
        url: `${IMAGES_URL}${image}`
      })),
      video_cover: []
    })
  }, [horse, form])

  const searchTimer = React.useRef<ReturnType<typeof setTimeout>>()

  const onAddressSearch = (value: string) => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      if (!value) return
      try {
        const response = await appApi.getAddresses({ address: value })
        setAddresses(response.data.suggestions)
      } catch (e) {
        showServerError(e)
      }
    }, SEARCH_DELAY)
  }

  const addressOptions = React.useMemo(() => {
    const options = addresses.map((item) => ({
      value: JSON.stringify({
        name: item.value,
        lat: Number(item.data.geo_lat),
        lng: Number(item.data.geo_lon)
      }),
      label: item.value
    }))
    if (
      currentLocation &&
      !options.some((option) => option.value === currentLocation.value)
    ) {
      options.unshift(currentLocation)
    }
    return options
  }, [addresses, currentLocation])

  const removeDocument = (fileName: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.fileName !== fileName))
    setToRemoveDocuments((prev) => [...prev, fileName])
  }

  const handleSubmit = async (values: any) => {
    try {
      setIsSubmitting(true)

      const imagesFileList: UploadFile[] = values.images ?? []
      const keptImages = imagesFileList
        .map(getExistingFileName)
        .filter(Boolean) as string[]
      const newImages = imagesFileList
        .map(getRawFile)
        .filter(Boolean) as File[]
      const toRemoveImages = horse.images.filter(
        (image) => !keptImages.includes(image)
      )

      const newVideoCover = getRawFile((values.video_cover ?? [])[0])

      const newDocuments: { name: string; file?: UploadFile[] }[] =
        values.newDocuments ?? []
      const documentsToAdd = newDocuments
        .map((doc) => ({ name: doc.name, file: getRawFile(doc.file?.[0]) }))
        .filter((doc) => doc.name && doc.file)

      const payload: IUpdateHorseRequest = {
        name: values.name.trim(),
        sex: values.sex,
        breed: values.breed,
        suit: values.suit,
        category: values.category,
        birthDate: dayjs(values.birthDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        withersHeight: values.withersHeight,
        price: values.price,
        location: JSON.parse(values.location),
        desc: values.desc?.trim(),
        reg_number: values.reg_number?.trim(),
        genealogy: values.genealogy?.trim(),
        birthplace: values.birthplace?.trim(),
        competition_history: (values.competition_history ?? []).map(
          (item: { name: string; place: string }) => ({
            name: item.name,
            place: String(item.place)
          })
        ),
        communication: {
          tel: normalizePhone(values.tel),
          preferredCommunication: communicationKeys.reduce(
            (acc, key) => ({
              ...acc,
              [key]: (values.preferredCommunication ?? []).includes(key)
            }),
            {} as IPreferredCommunication
          )
        },
        ...(newImages.length && { images: newImages }),
        ...(toRemoveImages.length && { toRemoveImages }),
        ...(newVideoCover && { video_cover: newVideoCover }),
        ...(toRemoveVideoCover && { toRemoveVideoCover }),
        ...(documentsToAdd.length && {
          documents: documentsToAdd.map((doc) => doc.file as File),
          documentsNames: documentsToAdd.map((doc) => doc.name)
        }),
        ...(toRemoveDocuments.length && { toRemoveDocuments })
      }

      const { data } = await horsesApi.updateHorse(horse._id, payload)
      const isSentToModeration =
        data?.status === EHorseStatus.MODERATION && horse.status !== EHorseStatus.MODERATION
      showMessage(
        'success',
        isSentToModeration ? 'Изменения сохранены и отправлены на модерацию' : 'Данные лошади обновлены',
        ''
      )
      onSaved()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form form={form} layout='vertical' onFinish={handleSubmit}>
      <Typography.Title level={5}>Основная информация</Typography.Title>

      <Form.Item
        label='Кличка'
        name='name'
        rules={[{ required: true, message: 'Заполните кличку' }]}
      >
        <Input placeholder='Кличка лошади' />
      </Form.Item>

      <Form.Item
        label='Пол'
        name='sex'
        rules={[{ required: true, message: 'Выберите пол' }]}
      >
        <Select
          placeholder='Выберите пол'
          options={Object.values(ESex).map((sex) => ({
            value: sex,
            label: localeSex[sex]
          }))}
        />
      </Form.Item>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Form.Item
          label='Порода'
          name='breed'
          rules={[{ required: true, message: 'Выберите породу' }]}
          style={{ flex: '1 1 200px' }}
        >
          <Select
            showSearch
            optionFilterProp='label'
            placeholder='Выберите породу'
            options={(formInformation?.breeds ?? []).map((item) => ({
              value: item._id,
              label: item.name
            }))}
          />
        </Form.Item>
        <Form.Item
          label='Масть'
          name='suit'
          rules={[{ required: true, message: 'Выберите масть' }]}
          style={{ flex: '1 1 200px' }}
        >
          <Select
            showSearch
            optionFilterProp='label'
            placeholder='Выберите масть'
            options={(formInformation?.suits ?? []).map((item) => ({
              value: item._id,
              label: item.name
            }))}
          />
        </Form.Item>
        <Form.Item
          label='Категория'
          name='category'
          rules={[{ required: true, message: 'Выберите категорию' }]}
          style={{ flex: '1 1 200px' }}
        >
          <Select
            showSearch
            optionFilterProp='label'
            placeholder='Выберите категорию'
            options={(formInformation?.categories ?? []).map((item) => ({
              value: item._id,
              label: item.name
            }))}
          />
        </Form.Item>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Form.Item
          label='Дата рождения'
          name='birthDate'
          rules={[{ required: true, message: 'Заполните дату рождения' }]}
          style={{ flex: '1 1 200px' }}
        >
          <DatePicker
            format='DD.MM.YYYY'
            placeholder='01.01.2020'
            style={{ width: '100%' }}
            disabledDate={(date) => date.isAfter(dayjs())}
          />
        </Form.Item>
        <Form.Item
          label='Высота в холке, см'
          name='withersHeight'
          rules={[{ required: true, message: 'Заполните высоту' }]}
          style={{ flex: '1 1 200px' }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder='145' />
        </Form.Item>
        <Form.Item
          label='Цена, ₽'
          name='price'
          rules={[{ required: true, message: 'Заполните цену' }]}
          style={{ flex: '1 1 200px' }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder='100000' />
        </Form.Item>
      </div>

      <Form.Item label='Описание' name='desc'>
        <Input.TextArea rows={4} placeholder='Расскажите подробнее' />
      </Form.Item>

      <Form.Item
        label='Местоположение'
        name='location'
        rules={[{ required: true, message: 'Укажите местоположение' }]}
      >
        <Select
          showSearch
          placeholder='Начните вводить адрес'
          notFoundContent={null}
          filterOption={false}
          onSearch={onAddressSearch}
          options={addressOptions}
        />
      </Form.Item>

      <Divider />
      <Typography.Title level={5}>Фото и видео</Typography.Title>

      <Uploader
        name='images'
        rules={[{ required: true, message: 'Добавьте хотя бы одно фото' }]}
        multiple
        maxCount={MAX_IMAGES}
        hint={`До ${MAX_IMAGES} фотографий, форматы JPG, JPEG, PNG, WebP`}
      />

      {videoCover && (
        <Space align='center' style={{ marginBottom: 16 }}>
          <Typography.Link
            href={`${IMAGES_URL}${videoCover}`}
            target='_blank'
            rel='noreferrer'
          >
            Текущее видео
          </Typography.Link>
          <Button
            danger
            type='text'
            icon={<DeleteOutlined />}
            onClick={() => setToRemoveVideoCover(horse.video_cover)}
          >
            Удалить
          </Button>
        </Space>
      )}

      <Form.Item
        label={videoCover ? 'Заменить видео' : 'Видео'}
        name='video_cover'
        valuePropName='fileList'
        getValueFromEvent={normalizeFile}
        extra='Короткий ролик до 7 секунд, формат MP4'
      >
        <Upload accept='video/mp4' maxCount={1} beforeUpload={() => false}>
          <Button icon={<UploadOutlined />}>Выбрать видео</Button>
        </Upload>
      </Form.Item>

      <Divider />
      <Typography.Title level={5}>Дополнительно</Typography.Title>

      <Form.Item label='Регистрационный номер' name='reg_number'>
        <Input placeholder='Введите номер' />
      </Form.Item>

      <Form.Item label='Родословная' name='genealogy'>
        <Input.TextArea rows={3} placeholder='Введите текст' />
      </Form.Item>

      <Form.Item label='Место рождения' name='birthplace'>
        <Input placeholder='Введите место рождения' />
      </Form.Item>

      <Form.Item
        label='Документы'
        extra='Ветеринарные чеки, снимки, справки: изображения или PDF'
      >
        {documents.map((doc) => (
          <Space key={doc.fileName} style={{ display: 'flex', marginBottom: 8 }}>
            <Typography.Link
              href={`${IMAGES_URL}${doc.fileName}`}
              target='_blank'
              rel='noreferrer'
            >
              {doc.name}
            </Typography.Link>
            <Button
              danger
              type='text'
              icon={<DeleteOutlined />}
              onClick={() => removeDocument(doc.fileName)}
            />
          </Space>
        ))}
        <Form.List name='newDocuments'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} align='baseline' style={{ display: 'flex' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'name']}
                    rules={[{ required: true, message: 'Название файла' }]}
                  >
                    <Input placeholder='Название файла' />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'file']}
                    valuePropName='fileList'
                    getValueFromEvent={normalizeFile}
                    rules={[{ required: true, message: 'Выберите файл' }]}
                  >
                    <Upload
                      accept='image/png,image/jpeg,image/jpg,application/pdf'
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>Файл</Button>
                    </Upload>
                  </Form.Item>
                  <Button
                    danger
                    type='text'
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />
                </Space>
              ))}
              <Button
                type='dashed'
                icon={<PlusOutlined />}
                onClick={() => add()}
              >
                Добавить документ
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='История соревнований'>
        <Form.List name='competition_history'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} align='baseline' style={{ display: 'flex' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'name']}
                    rules={[{ required: true, message: 'Название соревнования' }]}
                  >
                    <Input placeholder='Название соревнования' />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'place']}
                    rules={[{ required: true, message: 'Место' }]}
                  >
                    <Input placeholder='Место' />
                  </Form.Item>
                  <Button
                    danger
                    type='text'
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />
                </Space>
              ))}
              <Button
                type='dashed'
                icon={<PlusOutlined />}
                onClick={() => add()}
              >
                Добавить соревнование
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Divider />
      <Typography.Title level={5}>Контакты для связи</Typography.Title>

      <Form.Item
        label='Телефон'
        name='tel'
        rules={[
          { required: true, message: 'Укажите телефон' },
          {
            validator: (_, value) =>
              String(normalizePhone(value ?? '')).length === 11
                ? Promise.resolve()
                : Promise.reject(new Error('Укажите телефон полностью'))
          }
        ]}
      >
        <Input placeholder='+7 (900) 000-00-00' />
      </Form.Item>

      <Form.Item label='Способы связи' name='preferredCommunication'>
        <Checkbox.Group
          options={communicationKeys.map((key) => ({
            value: key,
            label: communicationLabels[key]
          }))}
        />
      </Form.Item>

      <Button type='primary' htmlType='submit' loading={isSubmitting}>
        Сохранить
      </Button>
    </Form>
  )
}
